import { heapTrackProperties } from '@m/utils/heap';

import type { StripeError } from '@stripe/stripe-js';

/**
 * Heap events for adding a payment method (card form, Payment Element, Apple Pay / Google Pay).
 *
 * Every submit gets an `attemptId` so its events can be joined into one funnel:
 *   SubmitStarted → StripeConfirmSucceeded | StripeConfirmFailed → SaveSucceeded | SaveFailed
 *
 * `setupIntentId` (never the client secret) joins attempts to `ClientSecretPrepared`, so a
 * prepared SetupIntent with no matching SubmitStarted is one that was created and never used.
 */

type HeapProps = Record<string, string | number | boolean | undefined>;

export type PaymentMethodComponent = 'StripeCardPaymentForm' | 'StripePaymentContainer';

/** `card` = card fields, `payment_element` = Stripe Payment Element, `wallet` = Express Checkout. */
export type PaymentMethodKind = 'card' | 'payment_element' | 'wallet';

export type StripeConfirmStage = 'elements_submit' | 'confirm' | 'create_token' | 'exception';

const MAX_ERROR_MESSAGE_LENGTH = 200;

const truncate = (message: string | undefined) => message?.slice(0, MAX_ERROR_MESSAGE_LENGTH);

const elapsedMs = (since: number) => Math.round(performance.now() - since);

const newAttemptId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

/** `seti_123_secret_abc` → `seti_123`. Returns undefined for anything that isn't an intent secret. */
export const intentIdFromClientSecret = (clientSecret: string | null | undefined) => {
  const match = clientSecret?.match(/^((?:seti|pi)_[^_]+)_secret_/);
  return match?.[1];
};

/** Seconds since the cached client secret was fetched, so stale intents show up in the data. */
export const secretAgeSeconds = (fetchedAt: number | undefined) =>
  fetchedAt ? Math.round((Date.now() - fetchedAt) / 1000) : undefined;

/** Stripe reports a reused / already-succeeded / canceled SetupIntent with this code. */
const STALE_INTENT_ERROR_CODES = new Set(['setup_intent_unexpected_state', 'resource_missing']);

const describeStripeError = (error: Partial<StripeError> | undefined): HeapProps => ({
  errorType: error?.type,
  errorCode: error?.code,
  declineCode: error?.decline_code,
  errorMessage: truncate(error?.message),
  isStaleIntent: !!error?.code && STALE_INTENT_ERROR_CODES.has(error.code),
});

/**
 * Accepts an Error (`ApiResponseError` / `FetchError` carry `status`), a legacy `apiFetch` failure
 * response (`{ error, status }`), or a plain message.
 */
const describeRequestError = (error: unknown): HeapProps => {
  const status =
    typeof error === 'object' && error !== null && 'status' in error ? Number(error.status) : NaN;
  let message: string;
  if (error instanceof Error) {
    message = error.message;
  } else if (typeof error === 'object' && error !== null) {
    const { error: apiError, statusText } = error as { error?: unknown; statusText?: unknown };
    message = String(apiError ?? statusText ?? 'Unknown error');
  } else {
    message = String(error);
  }
  return {
    httpStatus: Number.isFinite(status) && status > 0 ? status : undefined,
    errorMessage: truncate(message),
  };
};

// ── Client secret (SetupIntent creation) ────────────────────────────────────────

export const trackClientSecretPrepared = (props: { clientSecret: string; durationMs: number }) => {
  heapTrackProperties('PaymentMethod:ClientSecretPrepared', {
    intentId: intentIdFromClientSecret(props.clientSecret),
    durationMs: props.durationMs,
  });
};

export const trackClientSecretFailed = (props: {
  durationMs: number;
  error: unknown;
  /** The endpoint answered 200 but `stripeClientSecret` wasn't a secret (e.g. an error message). */
  invalidSecret: boolean;
}) => {
  heapTrackProperties('PaymentMethod:ClientSecretFailed', {
    durationMs: props.durationMs,
    invalidSecret: props.invalidSecret,
    ...describeRequestError(props.error),
  });
};

// ── Wallet button ───────────────────────────────────────────────────────────────

export const trackWalletAvailability = (props: {
  addedVia?: string;
  availablePaymentMethods: Record<string, boolean> | undefined;
  component: PaymentMethodComponent;
}) => {
  const available = Object.entries(props.availablePaymentMethods ?? {})
    .filter(([, isAvailable]) => isAvailable)
    .map(([name]) => name)
    .sort();
  heapTrackProperties('PaymentMethod:WalletAvailability', {
    component: props.component,
    addedVia: props.addedVia,
    availableWallets: available.length ? available.join(',') : 'none',
    hasApplePay: available.includes('applePay'),
    hasGooglePay: available.includes('googlePay'),
  });
};

export const trackWalletCancelled = (props: {
  addedVia?: string;
  component: PaymentMethodComponent;
}) => {
  heapTrackProperties('PaymentMethod:WalletCancelled', props);
};

// ── Submit attempt ──────────────────────────────────────────────────────────────

export type PaymentAttemptContext = {
  addedVia?: string;
  /** Card brand from the card field, when known before saving. */
  cardBrand?: string;
  /** Client secret used for this attempt, for the intent id and staleness. */
  clientSecret?: string;
  component: PaymentMethodComponent;
  /** Retrying the backend save with an already-confirmed SetupIntent (no new Stripe confirm). */
  isSaveRetry?: boolean;
  method: PaymentMethodKind;
  /** When the client secret was fetched (`dataUpdatedAt` of its query). */
  secretFetchedAt?: number;
  /** Express Checkout `expressPaymentType`, e.g. `apple_pay`, `google_pay`, `link`. */
  walletType?: string;
};

export type PaymentAttempt = ReturnType<typeof startPaymentAttempt>;

/**
 * Starts tracking one submit. Call the returned methods as the attempt progresses; each event
 * carries the attempt's context plus the time spent in that step.
 */
export const startPaymentAttempt = ({
  clientSecret,
  secretFetchedAt,
  ...context
}: PaymentAttemptContext) => {
  const attemptStartedAt = performance.now();
  let stepStartedAt = attemptStartedAt;

  const base: HeapProps = {
    ...context,
    attemptId: newAttemptId(),
    intentId: intentIdFromClientSecret(clientSecret),
    secretAgeSec: secretAgeSeconds(secretFetchedAt),
  };

  const track = (event: string, props: HeapProps = {}) => {
    heapTrackProperties(event, {
      ...base,
      ...props,
      stepDurationMs: elapsedMs(stepStartedAt),
      attemptDurationMs: elapsedMs(attemptStartedAt),
    });
    stepStartedAt = performance.now();
  };

  track('PaymentMethod:SubmitStarted');

  return {
    stripeConfirmSucceeded: (props: { saveSkipped?: boolean; setupIntentStatus?: string } = {}) =>
      track('PaymentMethod:StripeConfirmSucceeded', props),

    stripeConfirmFailed: (stage: StripeConfirmStage, error?: Partial<StripeError> | unknown) =>
      track('PaymentMethod:StripeConfirmFailed', {
        stage,
        ...(stage === 'exception'
          ? describeRequestError(error)
          : describeStripeError(error as Partial<StripeError> | undefined)),
      }),

    saveSucceeded: (paymentMethod?: { cardBrand?: string | null; wallet?: string | null }) =>
      track('PaymentMethod:SaveSucceeded', {
        savedCardBrand: paymentMethod?.cardBrand ?? undefined,
        savedWallet: paymentMethod?.wallet ?? undefined,
      }),

    saveFailed: (error: unknown) => track('PaymentMethod:SaveFailed', describeRequestError(error)),
  };
};
